import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { FieldArray as FormikFieldArray, useField } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import { isEmpty, isNil, path } from 'ramda'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { QuestionnaireQuestion } from '@/modules/questionnaire/components/questionnaire-question/QuestionnaireQuestion'
import { useArrayFieldNext } from '@/modules/questionnaire/hooks/useArrayFieldNext'
import { splitText } from '@/modules/questionnaire/utils/split-question-text'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { replaceAt } from '@/utils/array-replace-at'
import { FlexColumn, FlexRow, Margin } from '../../layout'
import { Button } from '../../primitives/button/Button'
import { Span } from '../../typography/Body'
import { H3 } from '../../typography/Heading'

import type { IQuestionnaireQuestion, QuestionType } from '@healthblocks-io/core'
import type { FieldArrayRenderProps } from 'formik'

type FieldArrayProps = {
  questionId: string
  data: IQuestionnaireQuestion
  onNext?: () => void
}

const FieldArray = ({ questionId, data, onNext = () => {} }: FieldArrayProps) => {
  const [editableIndex, setEditableIndex] = useState<number>()

  const [title] = useMemo(() => splitText(data.text), [data.text])

  const theme = useTheme()
  const [field, { value: formikValue }, fieldHelpers] = useField<Record<string, unknown>[]>(questionId)

  // Workaround for empty string values from older versions, and undefined when the field is not yet initialized
  const value = Array.isArray(formikValue) ? formikValue : []

  // Template variables
  // ------------------------------------------------------------------------- /

  const currentIndexIsValid = useMemo(() => {
    let isValid = true

    if (!data.fields || isNil(editableIndex)) {
      return isValid
    }

    for (const dataField of data.fields) {
      if (dataField.required) {
        const fieldValue = path([editableIndex, dataField.linkId], value)

        if (isEmpty(fieldValue)) {
          isValid = false
          break
        }

        const isStringValue = typeof fieldValue === 'string'
        const isArrayValue = Array.isArray(fieldValue)

        if ((isStringValue && fieldValue.includes('_custom')) || (isArrayValue && fieldValue.includes('_custom'))) {
          const customFieldValue = path(
            [editableIndex, `${dataField.linkId}${isStringValue ? fieldValue : '_custom'}`],
            value,
          )
          if (isEmpty(customFieldValue)) {
            isValid = false
            break
          }
        }
      }
    }

    return isValid
  }, [data, JSON.stringify(value), editableIndex])

  const emptyArrayValue = useMemo(() => {
    if (data.type === 'Array') {
      return data.fields?.reduce(
        (accumulator, current) => {
          accumulator[current.linkId] = ''

          return accumulator
        },
        {} as Record<string, unknown>,
      )
    }
  }, [data.type, data.fields])

  const handleArrayNext = useArrayFieldNext(data, questionId, onNext)

  // Effects
  // ------------------------------------------------------------------------- /

  /**
   * When the `_custom`-value is selected, an extra field is appened with key `${parentKey}_custom`.
   * Due to the internals of Formik, when filling in the `${parentKey}_custom`-field and then choosing a value other than the original `_custom`, the value in `${parentKey}_custom` is not removed.
   * This script looks for those scenario's and removes the key when it's no longer needed.
   */
  useEffect(() => {
    if (!isNil(editableIndex)) {
      const currentValue = value[editableIndex]

      if (currentValue) {
        const cleanedValue = Object.keys(currentValue)
          .filter((key) => !key.endsWith('_custom'))
          .reduce((accumulator: Record<string, unknown>, current: string) => {
            accumulator[current] = currentValue[current]

            const isArray = Array.isArray(accumulator[current])

            if (
              accumulator[current] === '_custom' ||
              (isArray && (accumulator[current] as string[]).includes('_custom'))
            ) {
              accumulator[`${current}_custom`] = currentValue[`${current}_custom`] ?? ''
            }

            return accumulator
          }, {})

        // Replace the cleaned value in the index
        fieldHelpers.setValue(replaceAt(value, editableIndex, cleanedValue))
      }
    }
  }, [JSON.stringify(value), editableIndex]) // Only compare the stringified version to prevent infinite loops.

  // Render Methods
  // ------------------------------------------------------------------------- /
  const renderAddEntryButton = useCallback(
    (arrayHelpers: FieldArrayRenderProps) => {
      return (
        <Button
          prefixIcon={['fal', 'plus']}
          variant='Secondary'
          type='button'
          onClick={() => {
            setEditableIndex(value?.length || 0)
            arrayHelpers.push(emptyArrayValue)

            setTimeout(() => {
              // [Fabian]: This isn't super pretty, but it gets the job done.
              const parentScrollView = document.querySelector(`#questionnaire-page`)

              if (parentScrollView) {
                parentScrollView.scrollTo({ top: window.innerHeight })
              }
            }, 0)
          }}
        >
          {t({
            id: 'field-array.add-item',
            comment: 'Label for the button to add an item to the FieldArray.',
            message: '{type} toevoegen',
            values: {
              type: title,
            },
          })}
        </Button>
      )
    },
    [emptyArrayValue, value?.length, title],
  )

  const renderArrayAnswer = useCallback(
    (value: Record<string, unknown>, index: number, arrayHelpers: FieldArrayRenderProps) => {
      return (
        <motion.div key={`${questionId}_answer_${value.value}`} {...getDropInProps()}>
          <FlexColumn gap={theme.UI.Spacing.Space2}>
            <FlexRow justifyContent='space-between'>
              <Span type='BodyMediumBold'>
                {title} {index + 1}
              </Span>

              <FontAwesomeIcon
                icon={['fal', 'trash-can']}
                onClick={() => {
                  arrayHelpers.remove(index)
                  setEditableIndex(undefined)
                }}
              />
            </FlexRow>
            <Span type='BodyLargeRegular' color={theme.Colors.Neutrals.Darker}>
              {Object.keys(value || [])
                .sort((a: string, b: string) => {
                  const aIndex = data.fields?.findIndex((value) => value.linkId === a) ?? -1
                  const bIndex = data.fields?.findIndex((value) => value.linkId === b) ?? -1

                  return aIndex - bIndex
                })
                .map((key) => {
                  // Label lookup based on the value
                  const questionObject = data.fields?.find((field) => field.linkId === key)

                  // Return early if 'Text'
                  if (questionObject?.type === 'Text') {
                    return value[key]
                  }

                  // Return early if 'MultiSelect'
                  if (questionObject?.type === 'MultiSelect') {
                    const customValue = value[`${key}_custom`]

                    return value[key]
                      ? (value[key] as Array<string>).join(', ').replace('_custom', customValue as string)
                      : ''
                  }

                  const answerOption = questionObject?.answerOption?.find(
                    (answerOption) => answerOption.value === value[key],
                  )

                  if (answerOption?.value === '_custom' && value[`${key}_custom`]) {
                    return `${answerOption?.label}: ${value[`${key}_custom`]}`
                  }

                  return answerOption?.label
                })
                .filter((value) => !!value)
                .join(' - ')}
            </Span>
          </FlexColumn>
        </motion.div>
      )
    },
    [theme, data.fields, questionId, title],
  )

  const renderArrayForm = useCallback(
    (index: number) => {
      return (
        <>
          <H3>
            {title} {index + 1}
          </H3>
          {data.fields?.map((dataField) => (
            <Fragment key={`${questionId}_${dataField.linkId}_${index}`}>
              <QuestionnaireQuestion
                nested
                data={{
                  ...dataField,
                  type:
                    dataField.type === 'QuickReplies'
                      ? ('Custom_QuickReplies' as QuestionType)
                      : (dataField.type as QuestionType),
                  linkId: `${questionId}.${index}.${dataField.linkId}`,
                  id: `${questionId}.${index}.${dataField.linkId}`,
                }}
                onManuallySubmitAnswer={() => {}}
                onNext={() => {}}
                onPrevious={() => {}}
              />

              <Margin mb={theme.UI.Spacing.Space3} />

              <AnimatePresence>
                {(() => {
                  const fieldValue = value[index]?.[dataField.linkId]
                  return fieldValue === '_custom' || (Array.isArray(fieldValue) && fieldValue.includes('_custom'))
                })() ? (
                  <motion.div {...getDropInProps()}>
                    <QuestionnaireQuestion
                      onManuallySubmitAnswer={() => {}}
                      nested
                      noMargin
                      key={`${questionId}_${dataField.linkId}_custom_${index}`}
                      data={{
                        type: 'Text' as QuestionType,
                        placeholder: t({
                          id: 'field-array.custom-value-placeholder',
                          comment: 'Placeholder for the custom FieldArray fields.',
                          message: 'Beschrijf de {type}',
                          values: {
                            type: dataField.linkId.toLowerCase(),
                          },
                        }),
                        text: '',
                        linkId: `${questionId}.${index}.${dataField.linkId}_custom`,
                        id: `${questionId}.${index}.${dataField.linkId}_custom`,
                        required: true,
                      }}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </Fragment>
          ))}

          <Margin mt={theme.UI.Spacing.Space4} />
          <Button
            variant='Secondary'
            prefixIcon={['fal', 'check']}
            disabled={!currentIndexIsValid}
            onClick={() => setEditableIndex(undefined)}
          >
            {t({
              id: 'field-array.save-item',
              comment: 'Label for the button to save an item to the FieldArray.',
              message: '{type} opslaan',
              values: {
                type: title,
              },
            })}
          </Button>
        </>
      )
    },
    [questionId, data.fields, field, title, theme],
  )

  return (
    <FlexColumn gap={theme.UI.Spacing.Space4}>
      <FormikFieldArray
        key={questionId}
        name={questionId}
        render={(arrayHelpers) => (
          <>
            <FlexColumn gap={theme.UI.Spacing.Space5}>
              <AnimatePresence mode='sync'>
                {value?.map((value: Record<string, unknown>, index: number) => (
                  <motion.div key={index} {...getDropInProps()} style={{ width: '100%' }}>
                    {index === editableIndex ? renderArrayForm(index) : renderArrayAnswer(value, index, arrayHelpers)}
                  </motion.div>
                ))}
              </AnimatePresence>

              {isNil(editableIndex) || value?.length === 0 ? renderAddEntryButton(arrayHelpers) : null}
            </FlexColumn>
          </>
        )}
      />
      <Button
        type='button'
        variant='Primary'
        onClick={handleArrayNext}
        disabled={!isNil(editableIndex) || (data.required && (!value || value.length === 0))}
      >
        {t({
          id: 'globals.continue',
          comment: 'Global for continue',
          message: 'Ga verder',
        })}
      </Button>
    </FlexColumn>
  )
}

export { FieldArray }
